import { ParsedAssessment } from "@/types/assessments";

/**
 * 解析 analysis_result 数据
 * 支持两种格式：
 * 1. 对象格式（jsonb 迁移后）：直接返回
 * 2. 字符串格式（向后兼容）：尝试 JSON.parse
 */
export const tryParseJson = (
  data?: string | object | null
): ParsedAssessment | null => {
  if (!data) return null;

  // 如果已经是对象，直接处理
  if (typeof data === "object") {
    const obj = data as Record<string, unknown>;

    // 检测新格式（两阶段）
    if (obj.stage_2_public) {
      const stage2 = obj.stage_2_public as Record<string, unknown>;
      const stage3 = obj.stage_3_private as Record<string, unknown> | undefined;

      // 确保 visual_evidence 是数组
      let visualEvidence: Array<{ body_part: string; symptom: string }> = [];
      if (Array.isArray(stage2.visual_evidence)) {
        visualEvidence = stage2.visual_evidence as Array<{
          body_part: string;
          symptom: string;
        }>;
      } else if (typeof stage2.visual_evidence === "string") {
        // 如果是字符串，尝试解析
        try {
          const parsed = JSON.parse(stage2.visual_evidence);
          if (Array.isArray(parsed)) {
            visualEvidence = parsed;
          }
        } catch {
          // 解析失败，保持空数组
        }
      }

      return {
        ...obj,
        title: stage2.assessment_title as string,
        severity: stage2.severity_level as "low" | "medium" | "high",
        confidence: stage2.confidence_score as number,
        summary: stage2.analysis_summary as string,
        findings: visualEvidence.map((e) => `${e.body_part}: ${e.symptom}`),
        causes: (stage3?.root_causes as string[]) || [],
        recommendations:
          (stage3?.care_steps as Array<{ action: string }>)?.map(
            (s) => s.action
          ) || [],
        stage_2_public: {
          ...stage2,
          visual_evidence: visualEvidence,
        } as ParsedAssessment["stage_2_public"],
        stage_3_private: stage3 as ParsedAssessment["stage_3_private"],
      } as ParsedAssessment;
    }

    // 旧格式对象直接返回
    return obj as ParsedAssessment;
  }

  // 字符串格式（向后兼容）
  const s = (data as string).trim();

  // 尝试直接解析
  try {
    const obj = JSON.parse(s);
    if (!obj || typeof obj !== "object") return null;

    // 检测新格式（两阶段）
    if (obj.stage_2_public) {
      // 转换为统一格式（向后兼容旧UI）
      const stage2 = obj.stage_2_public;

      // 确保 visual_evidence 是数组
      let visualEvidence: Array<{ body_part: string; symptom: string }> = [];
      if (Array.isArray(stage2.visual_evidence)) {
        visualEvidence = stage2.visual_evidence;
      } else if (typeof stage2.visual_evidence === "string") {
        try {
          const parsed = JSON.parse(stage2.visual_evidence);
          if (Array.isArray(parsed)) {
            visualEvidence = parsed;
          }
        } catch {
          // 解析失败，保持空数组
        }
      }

      return {
        ...obj,
        title: stage2.assessment_title,
        severity: stage2.severity_level,
        confidence: stage2.confidence_score,
        summary: stage2.analysis_summary,
        findings: visualEvidence.map(
          (e: { body_part: string; symptom: string }) =>
            `${e.body_part}: ${e.symptom}`
        ),
        causes: obj.stage_3_private?.root_causes || [],
        recommendations:
          obj.stage_3_private?.care_steps?.map(
            (s: { action: string }) => s.action
          ) || [],
        stage_2_public: {
          ...stage2,
          visual_evidence: visualEvidence,
        },
        stage_3_private: obj.stage_3_private,
      } as unknown as ParsedAssessment;
    }

    // 旧格式直接返回
    return obj as ParsedAssessment;
  } catch {
    // ignore
  }

  // 尝试从代码块中提取
  const fence = s.match(/```\s*(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fence?.[1]) {
    try {
      const obj = JSON.parse(fence[1].trim());
      if (obj?.stage_2_public) {
        const stage2 = obj.stage_2_public;

        // 确保 visual_evidence 是数组
        let visualEvidence: Array<{ body_part: string; symptom: string }> = [];
        if (Array.isArray(stage2.visual_evidence)) {
          visualEvidence = stage2.visual_evidence;
        } else if (typeof stage2.visual_evidence === "string") {
          try {
            const parsed = JSON.parse(stage2.visual_evidence);
            if (Array.isArray(parsed)) {
              visualEvidence = parsed;
            }
          } catch {
            // 解析失败，保持空数组
          }
        }

        return {
          ...obj,
          title: stage2.assessment_title,
          severity: stage2.severity_level,
          confidence: stage2.confidence_score,
          summary: stage2.analysis_summary,
          findings: visualEvidence.map(
            (e: { body_part: string; symptom: string }) =>
              `${e.body_part}: ${e.symptom}`
          ),
          causes: obj.stage_3_private?.root_causes || [],
          recommendations:
            obj.stage_3_private?.care_steps.map(
              (s: { action: string }) => s.action
            ) || [],
          stage_2_public: {
            ...stage2,
            visual_evidence: visualEvidence,
          },
          stage_3_private: obj.stage_3_private,
        } as unknown as ParsedAssessment;
      }
      return obj as ParsedAssessment;
    } catch {
      // ignore
    }
  }

  return null;
};

/**
 * 向后兼容地提取报告标题
 */
export const extractTitle = (
  raw?: string | object | null,
  fallback = "Analysis"
): string => {
  if (!raw) return fallback;
  const parsed = tryParseJson(raw);
  if (parsed?.title) return parsed.title;

  // 只有字符串才尝试提取第一行
  if (typeof raw === "string") {
    const firstLine = raw.split(/\r?\n/)[0];
    return (
      firstLine
        .replace(/^#+\s*/, "")
        .replace(/\*\*/g, "")
        .trim() || fallback
    );
  }

  return fallback;
};
